/**
 * Viedoc Clinic common expression helper functions.
 *
 * Adapted from ViedocClinicCommon.js (Viedoc4 server-side JINT sandbox helper).
 * JINT-vs-browser divergence:
 *   - `now()` / `today()`: the original reads `document.utcOffset` (injected by the
 *     JINT host) to apply the site timezone.  In the browser we have no site timezone
 *     offset available, so we fall back to the user's local time.  Callers that need
 *     site-timezone accuracy can supply a `utcOffsetMinutes` option when creating the
 *     sandbox context (reserved for future use).
 *   - `Array.prototype.contains` polyfill is handled by the shared arrayContains.js
 *     helper; it is not repeated here.
 *   - IE8 String.prototype.trim polyfill removed (not needed in modern browsers).
 *   - `parseRangeValue`, `createRangeValue`, `getRangeValue`, `inRange`, and
 *     `RangeComparatorEnum` are included for future RangeCheck support (Task #94340).
 */

// ---------------------------------------------------------------------------
// Date / time helpers
// ---------------------------------------------------------------------------

/**
 * Converts a date string ("yyyy-mm-dd" or "yyyy-MM-dd HH:mm") or year/month/day
 * components into a JavaScript Date object.
 * @param {string|number|Date} dateString
 * @param {number} [month]  1-based month; provide together with year and day
 * @param {number} [day]
 * @returns {Date}
 */
export function date(dateString, month, day) {
    if (dateString instanceof Date) return dateString;
    if (month && day) return new Date(dateString, month - 1, day);
    return parseDate(dateString);
}

/**
 * Parses a time string "HH:mm" into a Date whose date part is today.
 * @param {string} timeString
 * @returns {Date}
 */
export function time(timeString) {
    return parseTime(timeString);
}

/**
 * Parses a date string "yyyy-MM-dd HH:mm:ss" into a Date.
 * @param {string|Date} dateString
 * @returns {Date|null}
 */
export function parseDate(dateString) {
    if (!dateString) return null;
    if (dateString instanceof Date) return dateString;
    const parts = dateString.match(/(\d+)/g);
    return new Date(
        parts[0] || 0,
        (parts[1] - 1) || 0,
        parts[2] || 0,
        parts[3] || 0,
        parts[4] || 0,
        parts[5] || 0,
    );
}

/**
 * Parses a time string "HH:mm" into a Date whose date part is today.
 * @param {string} timeString
 * @returns {Date}
 */
export function parseTime(timeString) {
    const parts = timeString.match(/(\d+)/g);
    const $today = today();
    return new Date(
        $today.getFullYear(),
        $today.getMonth(),
        $today.getDate(),
        parts[0] || 0,
        parts[1] || 0,
        parts[2] || 0,
    );
}

/**
 * Returns the current date and time in the user's local timezone.
 * (Server-side JINT version reads site timezone from `document.utcOffset`; here we
 * use browser local time as the closest equivalent available in the browser context.)
 * @returns {Date}
 */
export function now() {
    return new Date();
}

/**
 * Returns today's date with the time part zeroed out.
 * @returns {Date}
 */
export function today() {
    const d = now();
    return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/**
 * Adds days to a date.
 * @param {Date} d
 * @param {number} daysToAdd
 * @returns {Date|undefined}
 */
export function addDays(d, daysToAdd) {
    if (!d) return d;
    const newDate = new Date(d);
    newDate.setDate(d.getDate() + daysToAdd);
    return newDate;
}

/**
 * Subtracts days from a date.
 * @param {Date} d
 * @param {number} daysToSub
 * @returns {Date}
 */
export function subDays(d, daysToSub) {
    const base = d || today();
    const newDate = new Date(base);
    newDate.setDate(base.getDate() - daysToSub);
    return newDate;
}

/**
 * Returns the age (decimal years) between two dates.
 * @param {Date} fromDate
 * @param {Date} toDate
 * @returns {number|null}
 */
export function age(fromDate, toDate) {
    if (!toDate || !fromDate) return null;
    const ageMilli = toDate - fromDate;
    return ageMilli / 1000 / 3600 / 24 / 365.25;
}

/**
 * Calculates BMI from weight (kg) and height (cm).
 * @param {number} weight
 * @param {number} height
 * @returns {number|null}
 */
export function bmi(weight, height) {
    if (weight <= 0 || height <= 0) return null;
    return weight / ((height / 100) * (height / 100));
}

/**
 * Calculates the difference in whole days between two dates (startDate - endDate).
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {number|null}
 */
export function days(startDate, endDate) {
    if (!startDate || !endDate) return null;
    const oneDay = 24 * 60 * 60 * 1000;
    const s = date(startDate);
    const e = date(endDate);
    return Math.round((s.getTime() - e.getTime()) / oneDay);
}

/**
 * Calculates the difference in whole hours between two date-time values.
 * @param {Date} startDateTime
 * @param {Date} endDateTime
 * @returns {number|null}
 */
export function hours(startDateTime, endDateTime) {
    if (!startDateTime || !endDateTime) return null;
    const oneHour = 60 * 60 * 1000;
    return Math.round((startDateTime.getTime() - endDateTime.getTime()) / oneHour);
}

/**
 * Calculates the difference in whole minutes between two date-time values.
 * @param {Date} startDateTime
 * @param {Date} endDateTime
 * @returns {number|null}
 */
export function minutes(startDateTime, endDateTime) {
    if (!startDateTime || !endDateTime) return null;
    const oneMinute = 60 * 1000;
    return Math.round((startDateTime.getTime() - endDateTime.getTime()) / oneMinute);
}

// ---------------------------------------------------------------------------
// Range helpers (included for future RangeCheck support — Task #94340)
// ---------------------------------------------------------------------------

/** @readonly @enum {string} */
export const RangeComparatorEnum = {
    NotSet: "NotSet",
    EqualTo: "EqualTo",
    LessThan: "LessThan",
    LessThanOrEqualTo: "LessThanOrEqualTo",
    GreaterThan: "GreaterThan",
    GreaterThanOrEqualTo: "GreaterThanOrEqualTo",
    InclusiveInBetween: "InclusiveInBetween",
};

function getDecimalCount(value) {
    if (value == null) return 0;
    const str = typeof value !== "string" ? String(value) : value;
    if (!str.includes(".")) return 0;
    return str.split(".")[1].length;
}

/**
 * Parses a range string (e.g. ">5", "<=10", "[1,5]") into a range object.
 * @param {string} value
 * @returns {object|null}
 */
export function parseRangeValue(value) {
    if (value == null || value === "") return null;
    const trimmed = value.toString().trim();
    const sw = (str) => trimmed.startsWith(str);
    if (sw("<=")) return createRangeValue(null, RangeComparatorEnum.LessThanOrEqualTo, trimmed.substring(2));
    if (sw("<"))  return createRangeValue(null, RangeComparatorEnum.LessThan, trimmed.substring(1));
    if (sw(">=")) return createRangeValue(trimmed.substring(2), RangeComparatorEnum.GreaterThanOrEqualTo, null);
    if (sw(">"))  return createRangeValue(trimmed.substring(1), RangeComparatorEnum.GreaterThan, null);
    if (sw("="))  return createRangeValue(trimmed.substring(1), RangeComparatorEnum.EqualTo, null);
    if (sw("[")) {
        const items = trimmed.replace(/[[\]]/g, "").split(",");
        return createRangeValue(items[0], RangeComparatorEnum.InclusiveInBetween, items[1]);
    }
    return null;
}

/**
 * @param {string|number|null} lower
 * @param {string} comparator
 * @param {string|number|null} upper
 * @returns {object|null}
 */
export function createRangeValue(lower, comparator, upper) {
    const lowerFloat = Number.parseFloat(lower);
    const lowerFormat = getDecimalCount(lower);
    const upperFloat = Number.parseFloat(upper);
    const upperFormat = getDecimalCount(upper);
    const result = { Lower: null, LowerFormat: 0, Comparator: null, Upper: null, UpperFormat: 0 };

    switch (comparator) {
        case RangeComparatorEnum.EqualTo:
            result.Lower = lowerFloat;
            result.LowerFormat = lowerFormat;
            result.Comparator = RangeComparatorEnum.EqualTo;
            result.Upper = lowerFloat;
            result.UpperFormat = lowerFormat;
            break;
        case RangeComparatorEnum.LessThan:
            result.Comparator = RangeComparatorEnum.LessThan;
            result.Upper = upperFloat;
            result.UpperFormat = upperFormat;
            break;
        case RangeComparatorEnum.LessThanOrEqualTo:
            result.Comparator = RangeComparatorEnum.LessThanOrEqualTo;
            result.Upper = upperFloat;
            result.UpperFormat = upperFormat;
            break;
        case RangeComparatorEnum.GreaterThan:
            result.Lower = lowerFloat;
            result.LowerFormat = lowerFormat;
            result.Comparator = RangeComparatorEnum.GreaterThan;
            break;
        case RangeComparatorEnum.GreaterThanOrEqualTo:
            result.Lower = lowerFloat;
            result.LowerFormat = lowerFormat;
            result.Comparator = RangeComparatorEnum.GreaterThanOrEqualTo;
            break;
        case RangeComparatorEnum.InclusiveInBetween:
            result.Lower = lowerFloat;
            result.LowerFormat = lowerFormat;
            result.Comparator = RangeComparatorEnum.InclusiveInBetween;
            result.Upper = upperFloat;
            result.UpperFormat = upperFormat;
            break;
        default:
            return null;
    }

    result.StringValue = getRangeValue(result);
    return result;
}

function getRangeValueFormattedNumber(value, limitDecimalsTo) {
    if (value == null) return "";
    let str = String(value);
    const prelDecimalCount = getDecimalCount(str);
    for (let i = prelDecimalCount; i < limitDecimalsTo; i++) {
        if (i === 0) str += ".";
        str += "0";
    }
    const valueDecimalCount = getDecimalCount(str);
    if (valueDecimalCount > limitDecimalsTo) {
        str = str.substring(0, str.length - (valueDecimalCount - limitDecimalsTo));
        if (str.endsWith(".")) str = str.substring(0, str.length - 1);
    }
    return str;
}

/**
 * Serialises a range object back to its string representation.
 * @param {object} rangeValue
 * @returns {string}
 */
export function getRangeValue(rangeValue) {
    if (rangeValue == null) return "";
    const lowerStr = getRangeValueFormattedNumber(rangeValue.Lower, rangeValue.LowerFormat);
    const upperStr = getRangeValueFormattedNumber(rangeValue.Upper, rangeValue.UpperFormat);
    const isValid = (n) => n != null && n !== "" && !Number.isNaN(Number(n));
    switch (rangeValue.Comparator) {
        case RangeComparatorEnum.EqualTo:            return isValid(lowerStr) ? `=${lowerStr}` : "";
        case RangeComparatorEnum.LessThan:           return isValid(upperStr) ? `<${upperStr}` : "";
        case RangeComparatorEnum.LessThanOrEqualTo:  return isValid(upperStr) ? `<=${upperStr}` : "";
        case RangeComparatorEnum.GreaterThan:        return isValid(lowerStr) ? `>${lowerStr}` : "";
        case RangeComparatorEnum.GreaterThanOrEqualTo: return isValid(lowerStr) ? `>=${lowerStr}` : "";
        case RangeComparatorEnum.InclusiveInBetween: return (isValid(lowerStr) && isValid(upperStr)) ? `[${lowerStr},${upperStr}]` : "";
        default: return "";
    }
}

/**
 * Returns true when numericValue is within the range described by rangeValue.
 * Returns true when either argument is null/empty (no constraint).
 * @param {string|object} rangeValue
 * @param {number} numericValue
 * @returns {boolean}
 */
export function inRange(rangeValue, numericValue) {
    if (rangeValue == null || rangeValue === "" || numericValue == null) return true;
    const rv = typeof rangeValue === "string" ? parseRangeValue(rangeValue) : rangeValue;
    const nv = Number.parseFloat(numericValue);
    if (rv == null || Number.isNaN(nv)) return false;
    const lo = Number.parseFloat(rv.Lower);
    const hi = Number.parseFloat(rv.Upper);
    switch (rv.Comparator) {
        case RangeComparatorEnum.EqualTo:            return nv === lo && nv === hi;
        case RangeComparatorEnum.LessThan:           return nv < hi;
        case RangeComparatorEnum.LessThanOrEqualTo:  return nv <= hi;
        case RangeComparatorEnum.GreaterThan:        return nv > lo;
        case RangeComparatorEnum.GreaterThanOrEqualTo: return nv >= lo;
        case RangeComparatorEnum.InclusiveInBetween: return nv >= lo && nv <= hi;
        default: return false;
    }
}

// ---------------------------------------------------------------------------
// Flat object of all helpers injected into the expression sandbox as named locals
// ---------------------------------------------------------------------------

/**
 * All helper functions available by bare name inside evaluated expressions.
 * Mirrors the globals that ViedocClinicCommon.js provides to the JINT host.
 */
export const clinicHelpers = {
    date,
    time,
    parseDate,
    parseTime,
    now,
    today,
    addDays,
    subDays,
    age,
    bmi,
    days,
    hours,
    minutes,
    parseRangeValue,
    createRangeValue,
    getRangeValue,
    inRange,
    RangeComparatorEnum,
};
